import { useEffect } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { LayoutDashboard, Users, AlertTriangle, Gift, FileText, MessageCircle, ShieldAlert, Layers, Megaphone, Cpu, ScrollText, Settings, Link2, Tags, UserPlus, PartyPopper, UserX, ListChecks, KeyRound, Package, Coins, BadgeCheck, FlaskConical, Send } from 'lucide-react';
import { OverlayScrollbars } from 'overlayscrollbars';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/cn';

/**
 * 어드민 페이지 전용 셸.
 * - 사이드바(좌) + 컨텐츠(우) 2분할
 * - DefaultLayout 의 헤더/푸터/광고/바텀네비를 사용하지 않음
 * - Phase 2 시점에서는 현재 구현된 4개 메뉴만 노출.
 *   Phase 3+ 에서 신규 메뉴(게시글/댓글/키워드/공지/게시판/시스템)가 추가될 때 MENU 에 항목을 추가한다.
 */

const ROLE_LEVEL: Record<string, number> = {
  readonly: 1,
  viewer: 1,
  content: 2,
  super: 3,
};

type MinRole = 'viewer' | 'content' | 'super';

interface MenuItem {
  label: string;
  path: string;
  icon: typeof LayoutDashboard;
  minRole: MinRole;
}

const MENU: readonly MenuItem[] = [
  { label: '대시보드', path: '/admin', icon: LayoutDashboard, minRole: 'viewer' },
  { label: '할 일', path: '/admin/todos', icon: ListChecks, minRole: 'viewer' },
  { label: '유저 관리', path: '/admin/users', icon: Users, minRole: 'viewer' },
  { label: '게시글 관리', path: '/admin/posts', icon: FileText, minRole: 'viewer' },
  { label: '공시 관리', path: '/admin/disclosures', icon: Send, minRole: 'content' },
  { label: '댓글 관리', path: '/admin/comments', icon: MessageCircle, minRole: 'viewer' },
  { label: '신고 처리', path: '/admin/reports', icon: AlertTriangle, minRole: 'viewer' },
  { label: '키워드 필터', path: '/admin/keywords', icon: ShieldAlert, minRole: 'content' },
  { label: '공지사항', path: '/admin/notices', icon: Megaphone, minRole: 'content' },
  { label: '교환 처리', path: '/admin/exchanges', icon: Gift, minRole: 'content' },
  { label: '교환 상품 관리', path: '/admin/exchange-items', icon: Package, minRole: 'content' },
  { label: '포인트 정책', path: '/admin/points-policy', icon: Coins, minRole: 'viewer' },
  { label: '이벤트 관리', path: '/admin/events', icon: PartyPopper, minRole: 'content' },
  { label: '친구 추천 현황', path: '/admin/referrals', icon: UserPlus, minRole: 'super' },
  { label: '미접속 계정', path: '/admin/inactivity', icon: UserX, minRole: 'viewer' },
  { label: '약관 관리', path: '/admin/terms', icon: ScrollText, minRole: 'content' },
  { label: '게시판/카테고리', path: '/admin/boards', icon: Layers, minRole: 'super' },
  { label: '시스템/AI', path: '/admin/system', icon: Cpu, minRole: 'super' },
  { label: 'API 키 유효기간', path: '/admin/api-credentials', icon: KeyRound, minRole: 'super' },
  { label: '종목 태그', path: '/admin/tags', icon: Tags, minRole: 'content' },
  { label: '종목 뱃지', path: '/admin/ticker-badges', icon: BadgeCheck, minRole: 'content' },
  { label: 'UTM 빌더', path: '/admin/utm-builder', icon: Link2, minRole: 'content' },
  { label: '사이트 설정', path: '/admin/site-settings', icon: Settings, minRole: 'super' },
  { label: '테스트 관리', path: '/admin/test-runner', icon: FlaskConical, minRole: 'super' },
];

export function AdminLayout() {
  const me = useAuthStore((s) => s.me);
  const userLevel = ROLE_LEVEL[me?.adminRole ?? ''] ?? 0;

  const visibleMenu = MENU.filter((m) => userLevel >= ROLE_LEVEL[m.minRole]);

  // PC 에서만 body 스크롤에 OverlayScrollbars 적용 (DefaultLayout 과 동일 정책)
  useEffect(() => {
    const isPC = window.matchMedia('(min-width: 768px) and (hover: hover)').matches;
    if (!isPC) return;

    const osInstance = OverlayScrollbars(
      { target: document.body, cancel: { nativeScrollbarsOverlaid: false } },
      {
        scrollbars: {
          theme: 'os-theme-sisegonggan',
          autoHide: 'leave',
          autoHideDelay: 800,
        },
      },
    );

    return () => osInstance.destroy();
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-[var(--color-bg)] md:block">
      {/* 사이드바 — 모바일: 상단 가로 스크롤 / PC: 화면 좌측에 완전히 고정 (스크롤해도 절대 움직이지 않음) */}
      <aside
        className={cn(
          'border-b border-[var(--color-border)] bg-[var(--color-surface)]',
          // PC: viewport 기준 fixed 고정. 사이드바 자체는 내부 세로 스크롤 허용.
          'md:fixed md:inset-y-0 md:left-0 md:z-30 md:w-60 md:overflow-y-auto md:border-b-0 md:border-r',
        )}
        aria-label="어드민 메뉴"
      >
        <div className="hidden p-4 md:block">
          <h1 className="text-base font-bold text-[var(--color-text)]">시세공간 어드민</h1>
          <p className="mt-1 text-xs text-[var(--color-text-sub)]">
            {me?.nickname} · {me?.adminRole}
          </p>
        </div>
        <nav className="flex gap-1 overflow-x-auto p-2 md:flex-col md:gap-1 md:overflow-x-visible md:p-2">
          {visibleMenu.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/admin'}
                className={({ isActive }) =>
                  cn(
                    'flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-[var(--color-primary)] text-[var(--color-primary-inv)] font-semibold'
                      : 'text-[var(--color-text)] hover:bg-[var(--color-bg)]',
                  )
                }
              >
                <Icon className="h-4 w-4" aria-hidden />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* 메인 — 사이드바 fixed 만큼 PC 에서 좌측 여백(ml-60) 확보 */}
      <main className="flex-1 overflow-x-auto p-4 md:ml-60 md:p-6">
        <Outlet />
      </main>
    </div>
  );
}
